import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { PopoverController, AlertController, ModalController } from '@ionic/angular';
import { ProfileModalPage } from '../profile-modal/profile-modal.page';
import { employeeMenu, patientMenu, scientistMenu } from '../../interfaces/menus';

@Component({
  selector: 'app-mobile-menu-popover',
  templateUrl: './mobile-menu-popover.component.html',
  styleUrls: ['./mobile-menu-popover.component.scss']
})
export class MobileMenuPopoverComponent implements OnInit {
  @Input() appPages: any[] = [];
  @Input() isDarkmode: boolean = false;
  @Input() isFoodOnly: boolean = false; // If true, only show food menu items
  
  public openDropdowns: { [key: string]: boolean } = {};
  public showSettings = false;

  constructor(
    private router: Router,
    private popoverController: PopoverController,
    private alertController: AlertController,
    private modalController: ModalController
  ) {}

  ngOnInit() {
    // Load dark mode state from localStorage or use input
    try {
      const storedDarkMode = localStorage.getItem('darkMode');
      if (storedDarkMode !== null) {
        this.isDarkmode = JSON.parse(storedDarkMode);
      } else if (this.isDarkmode === undefined || this.isDarkmode === null) {
        // Default to light mode if nothing is set
        this.isDarkmode = false;
      }
      // Apply theme immediately - ensure light mode is set if not dark
      const currentTheme = document.body.getAttribute('data-theme');
      if (!currentTheme || (currentTheme === 'dark' && !this.isDarkmode)) {
        document.body.setAttribute('data-theme', this.isDarkmode ? 'dark' : 'light');
      } else if (currentTheme === 'light' && this.isDarkmode) {
        document.body.setAttribute('data-theme', 'dark');
      }
    } catch (e) {
      console.error('Error loading dark mode:', e);
      this.isDarkmode = false;
      document.body.setAttribute('data-theme', 'light');
    }
    
    // Load menu pages if not provided or if empty
    if (!this.appPages || this.appPages.length === 0) {
      try {
        const storedMenu = localStorage.getItem('appMenu');
        if (storedMenu) {
          const parsedMenu = JSON.parse(storedMenu);
          if (parsedMenu && parsedMenu.length > 0) {
            this.appPages = parsedMenu;
          }
        }
      } catch (e) {
        console.error('Error loading menu:', e);
      }
    }
    
    // If still empty, log a warning
    if (!this.appPages || this.appPages.length === 0) {
      console.warn('Menu is empty. Please select a profile from settings.');
    }
    
    // If food only mode, auto-expand the food menu
    if (this.isFoodOnly && this.appPages && this.appPages.length > 0) {
      const foodMenu = this.appPages.find((page: any) => page.title === 'Food & Catering');
      if (foodMenu && foodMenu.subPages && foodMenu.subPages.length > 0) {
        this.openDropdowns['Food & Catering'] = true;
      }
    }
  }

  toggleDropdown(pageTitle: string) {
    this.openDropdowns[pageTitle] = !this.openDropdowns[pageTitle];
  }

  isDropdownOpen(pageTitle: string): boolean {
    return this.openDropdowns[pageTitle] || false;
  }

  async closePopover() {
    await this.popoverController.dismiss();
  }

  async selectProfile() {
    await this.closePopover();
    await this.profilePopup(true);
  }

  async editMenu() {
    await this.closePopover();
    await this.customizeMenu(0, false);
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
  }

  toggleTheme() {
    this.isDarkmode = !this.isDarkmode;
    // Force set the theme attribute
    document.body.setAttribute('data-theme', this.isDarkmode ? 'dark' : 'light');
    localStorage.setItem('darkMode', JSON.stringify(this.isDarkmode));
    
    // Force a re-render by toggling a class
    document.body.classList.toggle('dark-mode', this.isDarkmode);
    document.body.classList.toggle('light-mode', !this.isDarkmode);
    
    // Trigger event for other components
    window.dispatchEvent(new CustomEvent('darkModeChanged', { detail: { isDark: this.isDarkmode } }));
  }

  async profilePopup(allowDismiss = false) {
    const alert = await this.alertController.create({
      header: 'Please choose a profile:',
      subHeader: 'Here you can select a predefined profile for the side menu. If you press "Custom View" then you can create your own profile. The profile can be changed at any time.',
      backdropDismiss: allowDismiss,
      buttons: [
        {
          text: 'Employee',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(employeeMenu));
            window.location.reload();
          }
        },
        {
          text: 'Scientist',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(scientistMenu));
            window.location.reload();
          }
        },
        {
          text: 'Patient',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(patientMenu));
            window.location.reload();
          }
        },
        {
          text: 'Custom View',
          handler: () => {
            this.customizeMenu();
          }
        }
      ]
    });
    await alert.present();
  }

  async customizeMenu(profile = 0, showBack = true) {
    const modal = await this.modalController.create({
      backdropDismiss: false,
      component: ProfileModalPage,
      componentProps: {
        profile: profile,
        showBack: showBack
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data && data.change) {
      if (data.menu != null) {
        this.appPages = data.menu;
        localStorage.setItem('appMenu', JSON.stringify(data.menu));
      } else {
        this.profilePopup();
      }
    }
  }

  toggleDarkMode(event: any) {
    const isDark = event.detail.checked;
    this.isDarkmode = isDark;
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('darkMode', JSON.stringify(isDark));
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('darkModeChanged', { detail: { isDark } }));
  }

  async navigate(url: string) {
    await this.closePopover();
    this.router.navigate([url]);
  }
}

